import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import { useLoading } from 'hooks/useLoading';
import ExperienceService from 'services/ExperienceService';
import ImageService from 'services/ImageService';
import IExperience from 'interfaces/IExperience';
import IBanner from 'interfaces/IBanner';
import MainTitle from 'components/MainTitle';
import Heading from 'components/Heading';
import Button from 'components/Button';
import Input from 'components/Input/Input';
import ConfirmationModal from 'components/ConfirmationModal';
import MessageModal from 'common/MessageModal';
import editIcon from 'assets/images/editIcon.svg';
import trashIcon from 'assets/images/trashIcon.svg';
import dragIcon from 'assets/images/dragIcon.svg';
import {
  SettingsList,
  SettingsListItem,
  SettingsListItemButton,
  SettingsListItemButtonList,
  SettingsListItemTitle,
} from 'components/List/styles';
import {
  AddImageButton,
  BannerImage,
  CarouselHeader,
  CloseImageButton,
  Paragraph,
  Section,
  SettingsListContainer,
  Video,
} from 'features/Config/components/Home/styles';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import styled from 'styled-components';

const Container = styled.div`
  margin: 0 auto 50px auto;
  width: 100%;
  padding: 0 50px;

  @media (max-width: 980px) {
    padding: 0 16px;
  }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #020c90;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    text-decoration: underline;
  }
`;

const ExperienceCarouselForm = (): JSX.Element => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { openLoading, closeLoading } = useLoading();

  const [experience, setExperience] = useState<IExperience | null>(null);
  const [banners, setBanners] = useState<IBanner[]>([]);
  const [currentBanners, setCurrentBanners] = useState<IBanner[]>([]);
  const [editingBanners, setEditingBanners] = useState(false);
  const [fileIndex, setFileIndex] = useState(-1);
  const [files, setFiles] = useState<{ [key: string]: File | undefined }>({});
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [targetId, setTargetId] = useState<number | string>(0);
  const [messageModal, setMessageModal] = useState(false);
  const [messageModalText, setMessageModalText] = useState('');

  const inputFile = useRef<HTMLInputElement>(null);
  const experienceService = new ExperienceService();
  const imageService = new ImageService();

  const fetchExperience = async () => {
    const expId = Number(id);
    if (Number.isNaN(expId)) return;

    try {
      openLoading();
      const data = await experienceService.findOne(expId);
      setExperience(data);

      const expBanners = (data.banners || []).map((b, idx) => ({
        id: b.id || idx + 1,
        title: b.title || '',
        description: b.description || '',
        image: b.image || '',
        link: b.link || '',
      }));

      setBanners(expBanners);
      setCurrentBanners(JSON.parse(JSON.stringify(expBanners)));
    } catch (error) {
      console.error('Erro ao carregar experiência:', error);
    } finally {
      closeLoading();
    }
  };

  useEffect(() => {
    fetchExperience();
  }, [id]);

  const showModal = (message: string) => {
    setMessageModalText(message);
    setMessageModal(true);
  };

  const handleInputFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event?.target?.files?.[0]) {
      return;
    }

    const file = event.target.files[0];
    const objectUrl = `${URL.createObjectURL(file)}#${file.name}`;

    setFiles({ ...files, [objectUrl]: file });
    updateBannerImage(objectUrl);
  };

  const updateBannerImage = (image: string, current = fileIndex) => {
    const updateBanners = banners.map((banner, index) => {
      if (current === index) {
        return { ...banner, image };
      }
      return { ...banner };
    });

    setBanners(updateBanners);
  };

  const addBanner = () => {
    setEditingBanners(true);
    const newBanner: IBanner = {
      id: Date.now(),
      description: '',
      image: '',
      link: '',
      title: '',
    };
    setBanners(current => [...current, newBanner]);
  };

  const deleteBanner = () => {
    setEditingBanners(true);
    setBanners(banners.filter(banner => banner.id !== targetId));
    setConfirmDeletion(false);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(banners);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setBanners(items);
  };

  const handleInputTitleChange = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    setEditingBanners(true);
    const updatedBanners = banners.map(banner => {
      if (banner.id === id) {
        return { ...banner, title: event.target.value };
      }
      return banner;
    });

    setBanners(updatedBanners);
  };

  const handleInputURLChange = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    setEditingBanners(true);
    const updatedBanners = banners.map(banner => {
      if (banner.id === id) {
        return { ...banner, link: event.target.value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const cancelBannersEditing = () => {
    setBanners(JSON.parse(JSON.stringify(currentBanners)));
    setEditingBanners(false);
  };

  const saveBannersEditing = async () => {
    const expId = Number(id);
    if (Number.isNaN(expId)) return;

    const emptyImage = banners.some(({ image }) => !image);

    if (emptyImage) {
      showModal('Selecione uma imagem para todos os itens do carrossel!');
      return;
    }

    openLoading();
    try {
      const processedBanners = [];

      for (const banner of banners) {
        let image = banner.image;
        const file = files[image];
        if (file) {
          image = await imageService.upload(file);
        }

        processedBanners.push({
          title: banner.title || '',
          description: banner.description || '',
          link: banner.link || '',
          image,
        });
      }

      await experienceService.update(expId, {
        banners: processedBanners as any,
      });

      setEditingBanners(false);
      showModal('Carrossel salvo com sucesso!');
      await fetchExperience();
    } catch (error) {
      console.error('Erro ao salvar carrossel:', error);
      showModal('Erro ao salvar o carrossel. Tente novamente.');
    } finally {
      closeLoading();
    }
  };

  return (
    <>
      <MainTitle>Configurações</MainTitle>
      <Container>
        <BackLink onClick={() => history.push('/admin/config/activities')}>← Voltar</BackLink>
        <Section>
          <CarouselHeader>
            <Heading>{experience ? `Carrossel: ${experience.title}` : 'Carrossel'}</Heading>
            <Grid container direction="row" justifyContent="flex-end" alignItems="center" columnSpacing={4}>
              <Grid item>
                {!editingBanners && (
                  <Button buttonType="secondary" onClick={() => setEditingBanners(true)}>
                    Editar
                  </Button>
                )}
              </Grid>
              <Grid item>
                <Button onClick={addBanner}>Adicionar</Button>
              </Grid>
            </Grid>
          </CarouselHeader>

          <Paragraph>
            Para garantir a qualidade da visualização, as imagens do carrossel precisam ter dimensão mínima de 1366 x
            600 pixels (a ordem das imagens no carrossel será a mesma mostrada abaixo).
            <br />
            Vídeos devem estar no formato MP4.
          </Paragraph>

          <SettingsListContainer>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="carousel-list" type="list" direction="vertical" isDropDisabled={!editingBanners}>
                {provided => (
                  <SettingsList className="carousel-list" ref={provided.innerRef} {...provided.droppableProps}>
                    {banners.map((banner, index) => (
                      <Draggable
                        key={banner.id}
                        draggableId={banner.id.toString()}
                        index={index}
                        isDragDisabled={!editingBanners}>
                        {provided => (
                          <SettingsListItem
                            key={banner.id}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}>
                            {editingBanners && (
                              <SettingsListItemTitle>
                                <div>
                                  <img draggable={false} src={dragIcon} alt="icon for drag item" />
                                </div>
                              </SettingsListItemTitle>
                            )}

                            <SettingsListItemTitle>
                              <BannerImage
                                style={{
                                  ...(banner.image && {
                                    background: `url(${banner.image}) center center / cover no-repeat`,
                                  }),
                                  ...(!banner.image && { backgroundColor: '#d9d9d9' }),
                                }}>
                                {banner.image && banner.image.endsWith('.mp4') && (
                                  <Video src={banner?.image} autoPlay loop muted playsInline />
                                )}
                                {editingBanners &&
                                  (!banner.image ? (
                                    <div className="add-image-button-container">
                                      <AddImageButton
                                        onClick={() => {
                                          inputFile?.current?.click();
                                          setFileIndex(index);
                                        }}>
                                        +
                                      </AddImageButton>
                                    </div>
                                  ) : (
                                    <CloseImageButton onClick={() => updateBannerImage('', index)}>X</CloseImageButton>
                                  ))}
                              </BannerImage>
                            </SettingsListItemTitle>

                            <SettingsListItemTitle className="input-box">
                              {editingBanners ? (
                                <Input
                                  type="text"
                                  defaultValue={banner.title}
                                  onChange={e => handleInputTitleChange(e, banner.id)}
                                  className="input-title"
                                  placeholder="Digite o título do item"
                                  value={banner.title}
                                />
                              ) : (
                                banner.title || '- - -'
                              )}
                            </SettingsListItemTitle>

                            <SettingsListItemTitle className="input-box">
                              {editingBanners ? (
                                <Input
                                  type="text"
                                  defaultValue={banner.link}
                                  onChange={e => handleInputURLChange(e, banner.id)}
                                  className="input-link"
                                  placeholder="Digite o link de redirecionamento"
                                  value={banner.link}
                                />
                              ) : (
                                banner.link || '- - -'
                              )}
                            </SettingsListItemTitle>

                            <SettingsListItemButtonList>
                              <SettingsListItemButton
                                type="button"
                                onClick={() => {
                                  setConfirmDeletion(true);
                                  setTargetId(banner.id);
                                }}>
                                <img src={trashIcon} alt="icon for trash item" />
                              </SettingsListItemButton>
                            </SettingsListItemButtonList>
                          </SettingsListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </SettingsList>
                )}
              </Droppable>
            </DragDropContext>
          </SettingsListContainer>

          {editingBanners && (
            <Grid container direction="row" justifyContent="flex-end" alignItems="center" columnSpacing={4}>
              <Grid item>
                <Button buttonType="secondary" onClick={cancelBannersEditing}>
                  Cancelar
                </Button>
              </Grid>
              <Grid item>
                <Button onClick={saveBannersEditing}>Salvar</Button>
              </Grid>
            </Grid>
          )}
        </Section>
      </Container>

      <input
        type="file"
        ref={inputFile}
        style={{ display: 'none' }}
        accept="image/*,video/mp4"
        onChange={handleInputFileChange}
      />

      <ConfirmationModal
        open={confirmDeletion}
        title="EXCLUSÃO DE ITEM"
        message="Você tem certeza de que deseja excluir esse item?"
        onCancel={() => setConfirmDeletion(false)}
        onConfirm={deleteBanner}
      />

      <MessageModal
        isOpen={messageModal}
        title={messageModalText.includes('sucesso') ? 'Sucesso' : 'Erro'}
        message={messageModalText}
        onClose={() => setMessageModal(false)}
      />
    </>
  );
};

export default ExperienceCarouselForm;
